import React, { useEffect, useState } from "react";
import { Create } from "react-admin";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { CouponType } from "../../enums/CouponType";
import { ProductType } from "../../enums/ProductType";
import { DateTimePicker } from "@mui/x-date-pickers";
import { CouponServiceV2 } from "../../services/CuponServiceV2";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { useNavigate, useLocation } from "react-router-dom";
import { TimeEnum } from "../../enums/TimeEnum";
import AddDateInclusiveExclusiveInCoupon from "../Common/AddDateInclusiveExclusiveInCoupon";
import CouponAppPreview from "./CouponAppPreview";
import { formatDate } from "../../utils/DateTimeUtils";
import {
  WorkspaceContainer,
  WorkspacePanel,
  WorkspaceHeader,
  WorkspaceHeaderMeta,
  HeaderMetaGroup,
  HeaderMetaLabel,
  WorkspaceSection,
  SectionTitle,
  WorkSpaceTitleRow,
  WorkspaceIconBadge,
  WorkspaceTitle,
  FormGrid,
  FormFieldGroup,
  FormLabel,
  ThreeColGrid,
  StatusToggleSwitch,
  HorizontalLine,
  SectionTitleRow,
} from "../../styles/commonStyles";

const couponServiceV2 = new CouponServiceV2();

const categoryItems = [
  { category: "Sales", subCategory: ["Default Value in case the coupon is generated by System"] },
  { category: "Marketing", subCategory: ["Offline", "Online"] },
  { category: "Retention", subCategory: ["Winback", "Renewal", "Previous Bundle Credit"] },
];

interface Coupon {
  couponCode: string;
  couponValue: string;
  couponType: string;
  productType: string;
  usageLimitPerCustomer: Number;
  maximumUsageLimit: Number;
  minimumOrderValue: string;
  maximumDiscountAmount: string;
  couponHeader: string;
  couponDescription: string;
  isActive: boolean;
  visibleOnApp: boolean;
  userSet: any;
  areaSet: any;
  minimumTotalAfterDiscount: string;
  couponDisplayString?: string;
  termsAndConditions: string[];
  errorMessages: string;
  dateInclusionExclusionRule: any;
  userInclusionExclusionRule: any;
  timeInclusionExclusionRule: any;
  isCashbackEnabled: boolean;
  cashbackType: string;
  cashbackValue: string;
  cashbackExpiryDays: string;
}

// ── Shared input sx ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    padding: 0,
    border: "1px solid rgba(108, 124, 147, 0.2)",
    borderRadius: "6px",
    backgroundColor: "rgb(249, 250, 251)",
    fontSize: "14px",
    transition: "all 0.2s ease",
    "& fieldset": { display: "none" },
    "&:hover": { backgroundColor: "#F1F3F5" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      borderColor: "#1976d2",
      boxShadow: "0 0 0 3px rgba(25,118,210,0.1)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    fontSize: "14px",
  },
  "& .MuiInputAdornment-positionStart": {
    marginLeft: "12px",
    marginRight: "2px",
  },
  "& .MuiInputAdornment-positionEnd": {
    marginRight: "12px",
    marginLeft: "2px",
  },
};

const selectSx = {
  border: "1px solid rgba(108, 124, 147, 0.2)",
  borderRadius: "6px",
  backgroundColor: "rgb(249, 250, 251)",
  fontSize: "14px",
  "& .MuiSelect-select": { padding: "10px 14px", fontSize: "14px" },
  "& fieldset": { display: "none" },
  "&:hover": { backgroundColor: "#F1F3F5" },
  "&.Mui-focused": { backgroundColor: "#fff", boxShadow: "0 0 0 3px rgba(25,118,210,0.1)" },
};

const accSx = {
  boxShadow: "none",
  border: "1px solid rgba(108, 124, 147, 0.2)",
  borderRadius: "10px !important",
  overflow: "hidden",
  "&:before": { display: "none" },
  "&.Mui-expanded": { mt: 0, borderColor: "text.secondary" },
  transition: "border-color 0.2s",
};

const RuleTypeChip = ({ type }: { type: string }) => (
  <Chip
    label={type}
    size="small"
    color={type === "inclusion" ? "success" : "error"}
    sx={{ fontSize: "0.68rem", height: 20, fontWeight: 700, textTransform: "capitalize" }}
  />
);

// ── Component ─────────────────────────────────────────────────────────────────

const CouponEdit = () => {
  const defaultValue: Coupon = {
    couponCode: "", couponValue: "", couponType: "", productType: "",
    usageLimitPerCustomer: 0, maximumUsageLimit: 0,
    minimumOrderValue: "", maximumDiscountAmount: "",
    couponHeader: "", couponDescription: "",
    isActive: false, visibleOnApp: false,
    userSet: [], areaSet: [],
    minimumTotalAfterDiscount: "", couponDisplayString: "",
    termsAndConditions: [], errorMessages: "",
    dateInclusionExclusionRule: {}, userInclusionExclusionRule: {}, timeInclusionExclusionRule: {},
    isCashbackEnabled: false, cashbackType: "", cashbackValue: "", cashbackExpiryDays: "",
  };

  const [couponState, setCouponState] = useState<Coupon>(defaultValue);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [disableAreaSetButton, setDisableAreaSetButton] = useState<boolean>(false);
  const [reasonSubCategory, setReasonSubCategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [dateInclusiveExclusive, setDateInclusiveExclusive] = useState<boolean>(false);
  const [inclusiveData, setInclusiveData] = useState<any>(null);
  const [userInclusionExclusion, setUserInclusionExclusion] = useState<boolean>(false);
  const [disableUserInclusionExclusion, setDisableUserInclusionExclusion] = useState<boolean>(false);
  const [userInclusionExclusionData, setUserInclusionExclusionData] = useState<any>(null);
  const [timeInclusionExclusion, setTimeInclusionExclusion] = useState<boolean>(false);
  const [addTimeInclusion, setAddTimeInclusion] = useState<boolean>(false);
  const [selectedTimeEnums, setSelectedTimeEnums] = useState<any>([]);
  const [removeTimeRule, setRemoveTimeRule] = useState<boolean>(false);
  const [removeDateRule, setRemoveDateRule] = useState<boolean>(false);
  const [removeUserRule, setRemoveUserRule] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const couponId = location.pathname.split("/")[2];

  useEffect(() => {
    couponServiceV2.getOneCupon(Number(couponId)).then((res) => {
      const data = res?.data;
      data.userSet = data?.userSet && data?.userSet.map((x: number) => JSON.stringify(x) + "\n").join().replaceAll(",", "");
      data.areaSet = data?.areaSet && data?.areaSet.map((x: number) => JSON.stringify(x) + "\n").join().replaceAll(",", "");
      const selectedCat = data?.reason?.split(" ")[0];
      const subCatIndex = data?.reason?.indexOf(" ");
      const selectedSubCat = data?.reason?.substring(subCatIndex + 1);
      setCouponState(data);
      setSelectedCategory(selectedCat);
      setReasonSubCategory(selectedSubCat);
      setExpiryDate(data?.expiresAt);
      setStartDate(data?.startsAt);
    });
  }, [couponId]);

  const validate = () => {
    const errors: any = {};
    if (Number(couponState?.couponValue) < 0) errors.couponValue = "Must be greater than 0";
    if (couponState?.couponType) errors.couponType = "Required";
    if (couponState?.productType) errors.productType = "Required";
    return errors;
  };

  const handleSubmit = () => {
    if (validate()) {
      // Normalize user/area targeting: send null (unrestricted) when empty, never an empty
      // array. An empty areaSet ([]) is treated by the backend as "matches no area", which
      // silently hides the coupon from every listing.
      const parseIdList = (raw: any): number[] =>
        raw ? String(raw).trim().split("\n").map((x: any) => Number(x)).filter((x: any) => !isNaN(x)) : [];
      const parsedUserSet = parseIdList(couponState?.userSet);
      const parsedAreaSet = parseIdList(couponState?.areaSet);
      const payload = {
        couponCode: couponState?.couponCode,
        couponValue: couponState?.couponValue,
        couponType: couponState?.couponType,
        productType: couponState?.productType,
        usageLimitPerCustomer: Number(couponState?.usageLimitPerCustomer),
        maximumUsageLimit: Number(couponState?.maximumUsageLimit),
        minimumOrderValue: couponState?.minimumOrderValue,
        maximumDiscountAmount: couponState?.maximumDiscountAmount,
        couponHeader: couponState?.couponHeader,
        couponDescription: couponState?.couponDescription,
        reason: selectedCategory + " " + reasonSubCategory,
        isActive: couponState?.isActive.toString() === "true",
        visibleOnApp: couponState?.visibleOnApp.toString() === "true",
        expiresAt: expiryDate,
        startsAt: startDate,
        userSet: parsedUserSet.length ? parsedUserSet : null,
        areaSet: parsedAreaSet.length ? parsedAreaSet : null,
        minimumTotalAfterDiscount: couponState?.minimumTotalAfterDiscount ? Number(couponState.minimumTotalAfterDiscount) : null,
        couponDisplayString: couponState?.couponDisplayString,
        termsAndConditions: couponState?.termsAndConditions,
        errorMessages: couponState?.errorMessages,
        dateInclusionExclusionRule: removeDateRule
          ? null
          : inclusiveData || couponState?.dateInclusionExclusionRule,
        userInclusionExclusionRule: removeUserRule
          ? null
          : userInclusionExclusionData || couponState?.userInclusionExclusionRule,
        timeInclusionExclusionRule: removeTimeRule
          ? null
          : timeInclusionExclusion
            ? { type: addTimeInclusion ? "inclusion" : "exclusion", timeSlots: selectedTimeEnums }
            : couponState?.timeInclusionExclusionRule,
        isCashbackEnabled: couponState?.isCashbackEnabled,
        cashbackType: couponState?.isCashbackEnabled ? couponState?.cashbackType : null,
        cashbackValue: couponState?.isCashbackEnabled ? couponState?.cashbackValue : null,
        cashbackExpiryDays: couponState?.isCashbackEnabled && couponState?.cashbackExpiryDays
          ? Number(couponState.cashbackExpiryDays)
          : null,
      };
      couponServiceV2.updateCoupon(Number(couponId), payload).then((data) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Success", type: CustomSnackbarTypes.SUCCESS });
        if (data?.data?.id) navigate(`/CouponsV2/${data.data.id}/show`, { replace: true });
      });
    }
  };

  const isActive = couponState?.isActive.toString() === "true";
  const visibleOnApp = couponState?.visibleOnApp.toString() === "true";

  const hasDateRule = couponState?.dateInclusionExclusionRule && Object.keys(couponState.dateInclusionExclusionRule).length > 0;
  const hasUserRule = couponState?.userInclusionExclusionRule && Object.keys(couponState.userInclusionExclusionRule).length > 0;
  const hasTimeRule = couponState?.timeInclusionExclusionRule && Object.keys(couponState.timeInclusionExclusionRule).length > 0;

  const userCount = couponState?.userSet ? String(couponState.userSet).trim().split("\n").filter((x: string) => x.trim() && !isNaN(Number(x))).length : 0;
  const areaCount = couponState?.areaSet ? String(couponState.areaSet).trim().split("\n").filter((x: string) => x.trim() && !isNaN(Number(x))).length : 0;
  const targetingLabel = userCount > 0 || areaCount > 0
    ? [userCount > 0 && `${userCount} users`, areaCount > 0 && `${areaCount} areas`].filter(Boolean).join(", ")
    : "Not set";

  const rulesActive = [
    (hasDateRule || inclusiveData) && !removeDateRule && "date",
    (hasUserRule || disableUserInclusionExclusion) && !removeUserRule && "user",
    (hasTimeRule || timeInclusionExclusion) && !removeTimeRule && "time",
  ].filter(Boolean);
  const rulesLabel = rulesActive.length > 0 ? rulesActive.join(", ") : "None";

  const cashbackLabel = couponState?.isCashbackEnabled
    ? `${couponState?.cashbackType || "—"} · ${couponState?.cashbackValue || "—"}${couponState?.cashbackType === "PERCENT" ? "%" : " ₹"}${couponState?.cashbackExpiryDays ? ` · ${couponState.cashbackExpiryDays}d` : ""}`
    : "Disabled";

  const termCount = couponState?.termsAndConditions?.length ?? 0;
  const termsLabel = [termCount > 0 && `${termCount} term${termCount !== 1 ? "s" : ""}`, couponState?.errorMessages && "1 error msg"].filter(Boolean).join(", ") || "None";

  return (
    <Create title="Edit Coupon">
      <>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <WorkspaceContainer>

          {/* ── Header ── */}
          <WorkspaceHeader>
            <SectionTitleRow>
              <WorkspaceIconBadge>
                <LocalOfferOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </WorkspaceIconBadge>
              <div>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <WorkspaceTitle style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}>
                    {couponState?.couponCode || "Edit Coupon"}
                  </WorkspaceTitle>
                  {couponId && (
                    <Chip
                      label={`#${couponId}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.68rem", height: 20, borderRadius: 1, fontFamily: "monospace" }}
                    />
                  )}
                </Box>
                <Box display="flex" gap={0.75} mt={0.5} flexWrap="wrap">
                  {couponState?.couponType && (
                    <Chip label={couponState.couponType} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20, borderRadius: 1 }} />
                  )}
                  {couponState?.couponValue && (
                    <Chip label={couponState.couponValue} size="small" sx={{ fontSize: "0.68rem", height: 20, borderRadius: 1, fontWeight: 700 }} />
                  )}
                  {couponState?.productType && (
                    <Chip label={couponState.productType} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20, borderRadius: 1 }} />
                  )}
                </Box>
              </div>
            </SectionTitleRow>

            <WorkspaceHeaderMeta>
              <HeaderMetaGroup>
                <HeaderMetaLabel>Active</HeaderMetaLabel>
                <div style={{ cursor: "pointer" }} onClick={() => setCouponState((prev) => ({ ...prev, isActive: !isActive }))}>
                  <StatusToggleSwitch active={isActive}><span className="slider" /></StatusToggleSwitch>
                </div>
              </HeaderMetaGroup>
              <HeaderMetaGroup>
                <HeaderMetaLabel>Visible on App</HeaderMetaLabel>
                <div style={{ cursor: "pointer" }} onClick={() => setCouponState((prev) => ({ ...prev, visibleOnApp: !visibleOnApp }))}>
                  <StatusToggleSwitch active={visibleOnApp}><span className="slider" /></StatusToggleSwitch>
                </div>
              </HeaderMetaGroup>
            </WorkspaceHeaderMeta>
          </WorkspaceHeader>

          {/* ── Identity ── */}
          <WorkspacePanel>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Coupon Identity</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <FormFieldGroup>
              <FormLabel>Coupon Code</FormLabel>
              <TextField
                value={couponState?.couponCode || ""}
                fullWidth
                size="small"
                placeholder="e.g. SUMMER20"
                inputProps={{ style: { fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.08em" } }}
                onChange={(e) => setCouponState((prev) => ({ ...prev, couponCode: e.target.value.toUpperCase() }))}
                sx={fieldSx}
              />
            </FormFieldGroup>

            <ThreeColGrid style={{ marginTop: 16 }}>
              <FormFieldGroup>
                <FormLabel>Coupon Value</FormLabel>
                <TextField
                  value={couponState?.couponValue || ""}
                  type="number"
                  fullWidth
                  size="small"
                  onChange={(e) => setCouponState((prev) => ({ ...prev, couponValue: e.target.value }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Coupon Type</FormLabel>
                <Select
                  fullWidth
                  value={couponState?.couponType || ""}
                  displayEmpty
                  onChange={(e) => setCouponState((prev) => ({ ...prev, couponType: e.target.value }))}
                  sx={selectSx}
                >
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select type</em></MenuItem>
                  {Object.values(CouponType).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Product Type</FormLabel>
                <Select
                  fullWidth
                  value={couponState?.productType || ""}
                  displayEmpty
                  onChange={(e) => setCouponState((prev) => ({ ...prev, productType: e.target.value }))}
                  sx={selectSx}
                >
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select type</em></MenuItem>
                  {Object.values(ProductType).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
              </FormFieldGroup>
            </ThreeColGrid>
          </WorkspacePanel>

          {/* ── Scheduling ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Scheduling</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <FormGrid>
              <FormFieldGroup>
                <FormLabel>Start Date</FormLabel>
                <DateTimePicker
                  inputFormat="dd MMM yyyy HH:mm:ss"
                  value={startDate}
                  disableMaskedInput
                  onChange={(v) => setStartDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" sx={fieldSx} />}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Expiry Date</FormLabel>
                <DateTimePicker
                  inputFormat="dd MMM yyyy HH:mm:ss"
                  value={expiryDate}
                  disableMaskedInput
                  onChange={(v) => setExpiryDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" sx={fieldSx} />}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Reason Category</FormLabel>
                <Select
                  fullWidth
                  value={selectedCategory}
                  displayEmpty
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="" disabled><em style={{ color: "#999" }}>Select category</em></MenuItem>
                  {categoryItems.map((item) => <MenuItem key={item.category} value={item.category}>{item.category}</MenuItem>)}
                </Select>
              </FormFieldGroup>
              {selectedCategory && (
                <FormFieldGroup>
                  <FormLabel>Reason Sub-Category</FormLabel>
                  <Select
                    fullWidth
                    value={reasonSubCategory}
                    displayEmpty
                    onChange={(e) => setReasonSubCategory(e.target.value)}
                    sx={selectSx}
                  >
                    <MenuItem value="" disabled><em style={{ color: "#999" }}>Select sub-category</em></MenuItem>
                    {categoryItems.find((c) => c.category === selectedCategory)?.subCategory.map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </Select>
                </FormFieldGroup>
              )}
            </FormGrid>
          </WorkspacePanel>

          {/* ── Limits ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Limits</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <ThreeColGrid>
              <FormFieldGroup>
                <FormLabel>Min Order Value</FormLabel>
                <TextField
                  value={couponState?.minimumOrderValue || ""}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setCouponState((prev) => ({ ...prev, minimumOrderValue: e.target.value }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Max Discount</FormLabel>
                <TextField
                  value={couponState?.maximumDiscountAmount || ""}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setCouponState((prev) => ({ ...prev, maximumDiscountAmount: e.target.value }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Min Total After Discount</FormLabel>
                <TextField
                  value={couponState?.minimumTotalAfterDiscount || ""}
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  onChange={(e) => setCouponState((prev) => ({ ...prev, minimumTotalAfterDiscount: e.target.value }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
            </ThreeColGrid>
            <FormGrid style={{ marginTop: 16 }}>
              <FormFieldGroup>
                <FormLabel>Usage Limit / Customer</FormLabel>
                <TextField
                  value={couponState?.usageLimitPerCustomer || ""}
                  type="number"
                  fullWidth
                  size="small"
                  onChange={(e) => setCouponState((prev) => ({ ...prev, usageLimitPerCustomer: e.target.value as any }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
              <FormFieldGroup>
                <FormLabel>Max Total Usage</FormLabel>
                <TextField
                  value={couponState?.maximumUsageLimit || ""}
                  type="number"
                  fullWidth
                  size="small"
                  onChange={(e) => setCouponState((prev) => ({ ...prev, maximumUsageLimit: e.target.value as any }))}
                  sx={fieldSx}
                />
              </FormFieldGroup>
            </FormGrid>
          </WorkspacePanel>

          {/* ── Display ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection>
              <WorkSpaceTitleRow>
                <SectionTitle>Display</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <FormFieldGroup>
              <FormLabel>Coupon Header</FormLabel>
              <TextField
                value={couponState?.couponHeader || ""}
                fullWidth
                size="small"
                placeholder="Short headline shown to users"
                onChange={(e) => setCouponState((prev) => ({ ...prev, couponHeader: e.target.value }))}
                sx={fieldSx}
              />
            </FormFieldGroup>
            <Box mt={2}>
              <FormLabel>Description</FormLabel>
              <TextField
                value={couponState?.couponDescription || ""}
                multiline
                minRows={3}
                fullWidth
                size="small"
                placeholder="Detailed description of the coupon"
                onChange={(e) => setCouponState((prev) => ({ ...prev, couponDescription: e.target.value }))}
                sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
              />
            </Box>
            <Box mt={2}>
              <FormLabel>Display String</FormLabel>
              <TextField
                value={couponState?.couponDisplayString || ""}
                multiline
                minRows={2}
                fullWidth
                size="small"
                placeholder="Optional custom display text"
                onChange={(e) => setCouponState((prev) => ({ ...prev, couponDisplayString: e.target.value }))}
                sx={{ ...fieldSx, mt: "2px", "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
              />
            </Box>
          </WorkspacePanel>

          {/* ── Optional configuration (accordions) ── */}
          <WorkspacePanel style={{ marginTop: 16 }}>
            <WorkspaceSection style={{ marginBottom: 12 }}>
              <WorkSpaceTitleRow>
                <SectionTitle>Optional Configuration</SectionTitle>
              </WorkSpaceTitleRow>
            </WorkspaceSection>

            <Box display="flex" flexDirection="column" gap={1}>

              {/* Targeting */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: targetingLabel !== "Not set" ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">User & Area Targeting</Typography>
                  </Box>
                  <Typography variant="caption" color={targetingLabel === "Not set" ? "text.disabled" : "primary.main"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {targetingLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormGrid>
                    <FormFieldGroup>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <FormLabel>User Set</FormLabel>
                        <Button
                          size="small"
                          color={disableButton ? "success" : "inherit"}
                          variant="text"
                          sx={{ fontSize: "0.7rem", py: 0, minWidth: 0, textTransform: "none" }}
                          onClick={() => { setDisableButton(!disableButton); if (!disableButton) setCouponState((prev) => ({ ...prev, userSet: null })); }}
                        >
                          {disableButton ? "Enable" : "Disable"}
                        </Button>
                      </Box>
                      <TextField
                        placeholder="One user ID per line"
                        value={couponState?.userSet || ""}
                        disabled={disableButton}
                        multiline
                        minRows={4}
                        maxRows={8}
                        fullWidth
                        size="small"
                        helperText={userCount > 0 ? `${userCount} valid IDs` : "One ID per line"}
                        onChange={(e) => setCouponState((prev) => ({ ...prev, userSet: e.target.value }))}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                    <FormFieldGroup>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <FormLabel>Area Set</FormLabel>
                        <Button
                          size="small"
                          color={disableAreaSetButton ? "success" : "inherit"}
                          variant="text"
                          sx={{ fontSize: "0.7rem", py: 0, minWidth: 0, textTransform: "none" }}
                          onClick={() => { setDisableAreaSetButton(!disableAreaSetButton); if (!disableAreaSetButton) setCouponState((prev) => ({ ...prev, areaSet: null })); }}
                        >
                          {disableAreaSetButton ? "Enable" : "Disable"}
                        </Button>
                      </Box>
                      <TextField
                        placeholder="One area ID per line"
                        value={couponState?.areaSet || ""}
                        disabled={disableAreaSetButton}
                        multiline
                        minRows={4}
                        maxRows={8}
                        fullWidth
                        size="small"
                        helperText={areaCount > 0 ? `${areaCount} valid IDs` : "One ID per line"}
                        onChange={(e) => setCouponState((prev) => ({ ...prev, areaSet: e.target.value }))}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                  </FormGrid>
                </AccordionDetails>
              </Accordion>

              {/* Rules */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: rulesLabel !== "None" ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Inclusion / Exclusion Rules</Typography>
                  </Box>
                  <Typography variant="caption" color={rulesLabel === "None" ? "text.disabled" : "success.main"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {rulesLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>

                    {/* Date Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>Date Rule</FormLabel>
                        <Box display="flex" gap={1}>
                          {hasDateRule && !removeDateRule && (
                            <Button size="small" variant="text" color="error" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => { setRemoveDateRule(true); setInclusiveData(null); }}>
                              Remove
                            </Button>
                          )}
                          {removeDateRule && (
                            <Button size="small" variant="text" color="inherit" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => setRemoveDateRule(false)}>
                              Undo
                            </Button>
                          )}
                          {!removeDateRule && (
                            <Button size="small" variant={inclusiveData ? "contained" : "outlined"} color="primary"
                              sx={{ fontSize: "0.7rem", py: 0.25, textTransform: "none" }}
                              onClick={() => setDateInclusiveExclusive(true)}>
                              {hasDateRule || inclusiveData ? "Change" : "+ Add"}
                            </Button>
                          )}
                        </Box>
                      </Box>
                      {removeDateRule && (
                        <Box sx={{ p: 1.25, bgcolor: "error.lighter", borderRadius: 1.5, border: "1px dashed", borderColor: "error.main" }}>
                          <Typography variant="caption" color="error.main" fontWeight={600}>Date rule will be removed on save</Typography>
                        </Box>
                      )}
                      {hasDateRule && !removeDateRule && !inclusiveData && (
                        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5} fontSize="0.62rem" textTransform="uppercase" letterSpacing={0.8}>Current</Typography>
                          <Box display="flex" gap={0.75} flexWrap="wrap" mb={0.75}>
                            <RuleTypeChip type={couponState.dateInclusionExclusionRule.type} />
                            <Chip label={couponState.dateInclusionExclusionRule.period} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />
                          </Box>
                          {couponState.dateInclusionExclusionRule.period === "DAYS_OF_WEEK"
                            ? <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {couponState.dateInclusionExclusionRule.values?.map((v: string) => (
                                  <Chip key={v} label={v} size="small" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />
                                ))}
                              </Box>
                            : <Table size="small">
                                <TableHead><TableRow>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.7rem", fontWeight: 700 }}>Start</TableCell>
                                  <TableCell sx={{ py: 0.5, fontSize: "0.7rem", fontWeight: 700 }}>End</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                  {couponState.dateInclusionExclusionRule.values?.map((each: any, i: number) => (
                                    <TableRow key={i}>
                                      <TableCell sx={{ py: 0.5, fontSize: "0.7rem" }}>{each.startDate ? formatDate(each.startDate) : "—"}</TableCell>
                                      <TableCell sx={{ py: 0.5, fontSize: "0.7rem" }}>{each.endDate ? formatDate(each.endDate) : "—"}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                          }
                        </Box>
                      )}
                      {inclusiveData && (
                        <Box sx={{ p: 1.5, bgcolor: "success.main", borderRadius: 1.5, opacity: 0.92 }}>
                          <Typography variant="caption" fontWeight={600} color="white" display="block" mb={0.5} fontSize="0.62rem" textTransform="uppercase" letterSpacing={0.8}>New Rule</Typography>
                          <Box display="flex" gap={0.75} flexWrap="wrap" mb={0.75}>
                            <Chip label={inclusiveData.type} size="small" sx={{ fontSize: "0.68rem", height: 20, fontWeight: 700, bgcolor: "white", color: inclusiveData.type === "inclusion" ? "success.main" : "error.main" }} />
                            <Chip label={inclusiveData.period} size="small" sx={{ fontSize: "0.68rem", height: 20, bgcolor: "rgba(255,255,255,0.25)", color: "white" }} />
                          </Box>
                        </Box>
                      )}
                      {!hasDateRule && !inclusiveData && !removeDateRule && (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No date rule configured</Typography>
                      )}
                    </Box>

                    <HorizontalLine />

                    {/* User Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>User Rule</FormLabel>
                        <Box display="flex" gap={1} alignItems="center">
                          {hasUserRule && !removeUserRule && (
                            <Button size="small" variant="text" color="error" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => { setRemoveUserRule(true); setDisableUserInclusionExclusion(false); setUserInclusionExclusionData(null); }}>
                              Remove
                            </Button>
                          )}
                          {removeUserRule && (
                            <Button size="small" variant="text" color="inherit" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => setRemoveUserRule(false)}>
                              Undo
                            </Button>
                          )}
                          <FormControlLabel
                            sx={{ m: 0, gap: 0.5 }}
                            control={
                              <Switch
                                size="small"
                                checked={disableUserInclusionExclusion}
                                disabled={removeUserRule}
                                onChange={(e) => { setDisableUserInclusionExclusion(e.target.checked); if (e.target.checked) setRemoveUserRule(false); }}
                              />
                            }
                            label={<Typography variant="caption" color="text.secondary">Change</Typography>}
                          />
                        </Box>
                      </Box>
                      {removeUserRule && (
                        <Box sx={{ p: 1.25, bgcolor: "error.lighter", borderRadius: 1.5, border: "1px dashed", borderColor: "error.main" }}>
                          <Typography variant="caption" color="error.main" fontWeight={600}>User rule will be removed on save</Typography>
                        </Box>
                      )}
                      {hasUserRule && !disableUserInclusionExclusion && !removeUserRule && (
                        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5} fontSize="0.62rem" textTransform="uppercase" letterSpacing={0.8}>Current</Typography>
                          <Box display="flex" gap={0.75} flexWrap="wrap">
                            <RuleTypeChip type={couponState.userInclusionExclusionRule.type} />
                            <Chip label={couponState.userInclusionExclusionRule.rule} size="small" color="primary" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />
                          </Box>
                        </Box>
                      )}
                      {disableUserInclusionExclusion && (
                        <Box display="flex" gap={1.5} alignItems="center">
                          <Select size="small" value={String(userInclusionExclusion)} sx={{ ...selectSx, minWidth: 130 }}
                            onChange={(e) => {
                              const isInclusion = e.target.value === "true";
                              setUserInclusionExclusion(isInclusion);
                              setUserInclusionExclusionData({ type: isInclusion ? "inclusion" : "exclusion", rule: "FIRST_TIME_USER" });
                            }}
                          >
                            <MenuItem value="true">Inclusion</MenuItem>
                            <MenuItem value="false">Exclusion</MenuItem>
                          </Select>
                          <Chip label="FIRST_TIME_USER" size="small" color="primary" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                        </Box>
                      )}
                      {!hasUserRule && !disableUserInclusionExclusion && !removeUserRule && (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No user rule configured</Typography>
                      )}
                    </Box>

                    <HorizontalLine />

                    {/* Time Rule */}
                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <FormLabel>Time Rule</FormLabel>
                        <Box display="flex" gap={1} alignItems="center">
                          {hasTimeRule && !removeTimeRule && (
                            <Button size="small" variant="text" color="error" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => { setRemoveTimeRule(true); setTimeInclusionExclusion(false); }}>
                              Remove
                            </Button>
                          )}
                          {removeTimeRule && (
                            <Button size="small" variant="text" color="inherit" sx={{ fontSize: "0.7rem", py: 0, textTransform: "none" }}
                              onClick={() => setRemoveTimeRule(false)}>
                              Undo
                            </Button>
                          )}
                          <FormControlLabel
                            sx={{ m: 0, gap: 0.5 }}
                            control={
                              <Switch
                                size="small"
                                checked={timeInclusionExclusion}
                                disabled={removeTimeRule}
                                onChange={(e) => { setTimeInclusionExclusion(e.target.checked); if (e.target.checked) setRemoveTimeRule(false); }}
                              />
                            }
                            label={<Typography variant="caption" color="text.secondary">Change</Typography>}
                          />
                        </Box>
                      </Box>
                      {removeTimeRule && (
                        <Box sx={{ p: 1.25, bgcolor: "error.lighter", borderRadius: 1.5, border: "1px dashed", borderColor: "error.main" }}>
                          <Typography variant="caption" color="error.main" fontWeight={600}>Time rule will be removed on save</Typography>
                        </Box>
                      )}
                      {hasTimeRule && !timeInclusionExclusion && !removeTimeRule && (
                        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5} fontSize="0.62rem" textTransform="uppercase" letterSpacing={0.8}>Current</Typography>
                          <Box mb={0.75}><RuleTypeChip type={couponState.timeInclusionExclusionRule.type} /></Box>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {couponState.timeInclusionExclusionRule.timeSlots?.map((slot: string) => (
                              <Chip key={slot} label={slot} size="small" color="primary" variant="outlined" sx={{ fontSize: "0.68rem", height: 20 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {timeInclusionExclusion && (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <FormFieldGroup>
                            <FormLabel>Type</FormLabel>
                            <Select size="small" value={String(addTimeInclusion)} sx={{ ...selectSx, maxWidth: 180 }}
                              onChange={(e) => setAddTimeInclusion(e.target.value === "true")}>
                              <MenuItem value="true">Inclusion</MenuItem>
                              <MenuItem value="false">Exclusion</MenuItem>
                            </Select>
                          </FormFieldGroup>
                          <FormFieldGroup>
                            <FormLabel>Time Slots</FormLabel>
                            <Select
                              multiple
                              fullWidth
                              size="small"
                              value={selectedTimeEnums}
                              sx={selectSx}
                              onChange={(e) => setSelectedTimeEnums(e.target.value)}
                              renderValue={(selected: TimeEnum[]) => (
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {selected.map((v) => <Chip key={v} label={v} size="small" color="primary" sx={{ height: 20, fontSize: "0.68rem" }} />)}
                                </Box>
                              )}
                            >
                              {Object.entries(TimeEnum).map(([key, value]) => <MenuItem key={key} value={value}>{value}</MenuItem>)}
                            </Select>
                          </FormFieldGroup>
                        </Box>
                      )}
                      {!hasTimeRule && !timeInclusionExclusion && !removeTimeRule && (
                        <Typography variant="caption" color="text.disabled" fontStyle="italic">No time rule configured</Typography>
                      )}
                    </Box>

                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Cashback */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: couponState?.isCashbackEnabled ? "success.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Cashback</Typography>
                  </Box>
                  <Typography variant="caption" color={couponState?.isCashbackEnabled ? "success.main" : "text.disabled"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {cashbackLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={couponState?.isCashbackEnabled}
                        onChange={(e) => setCouponState((prev) => ({ ...prev, isCashbackEnabled: e.target.checked }))}
                        color="success"
                      />
                    }
                    label={<Typography variant="body2" fontWeight={500} fontSize="14px">Enable Cashback</Typography>}
                  />
                  {couponState?.isCashbackEnabled && (
                    <ThreeColGrid style={{ marginTop: 16 }}>
                      <FormFieldGroup>
                        <FormLabel>Cashback Type</FormLabel>
                        <Select
                          fullWidth
                          value={couponState?.cashbackType || ""}
                          displayEmpty
                          onChange={(e) => setCouponState((prev) => ({ ...prev, cashbackType: e.target.value }))}
                          sx={selectSx}
                        >
                          <MenuItem value="" disabled><em style={{ color: "#999" }}>Select</em></MenuItem>
                          <MenuItem value="FLAT">Flat (₹)</MenuItem>
                          <MenuItem value="PERCENT">Percent (%)</MenuItem>
                        </Select>
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <FormLabel>Cashback Value</FormLabel>
                        <TextField
                          value={couponState?.cashbackValue || ""}
                          type="number"
                          fullWidth
                          size="small"
                          helperText={couponState?.cashbackType === "PERCENT" ? "0–100" : "₹ amount"}
                          onChange={(e) => setCouponState((prev) => ({ ...prev, cashbackValue: e.target.value }))}
                          sx={fieldSx}
                        />
                      </FormFieldGroup>
                      <FormFieldGroup>
                        <FormLabel>Expiry Days</FormLabel>
                        <TextField
                          value={couponState?.cashbackExpiryDays || ""}
                          type="number"
                          fullWidth
                          size="small"
                          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                          onChange={(e) => setCouponState((prev) => ({ ...prev, cashbackExpiryDays: e.target.value }))}
                          sx={fieldSx}
                        />
                      </FormFieldGroup>
                    </ThreeColGrid>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Terms & Errors */}
              <Accordion disableGutters sx={accSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />} sx={{ minHeight: 50, py: 0, "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s" }}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: termCount > 0 ? "primary.main" : "text.disabled", flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} fontSize="14px">Terms & Error Messages</Typography>
                  </Box>
                  <Typography variant="caption" color={termCount > 0 ? "primary.main" : "text.disabled"} sx={{ mr: 0.5, alignSelf: "center" }}>
                    {termsLabel}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2.5, pb: 2.5, px: 2.5 }}>
                  <FormGrid>
                    <FormFieldGroup>
                      <FormLabel>Terms & Conditions</FormLabel>
                      <TextField
                        label=""
                        placeholder="One term per line"
                        value={couponState?.termsAndConditions?.join("\n") || ""}
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        helperText={`One term per line · ${termCount} term${termCount !== 1 ? "s" : ""}`}
                        onChange={(e) => setCouponState((prev) => ({ ...prev, termsAndConditions: e.target.value.split("\n") }))}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                    <FormFieldGroup>
                      <FormLabel>Error Message</FormLabel>
                      <TextField
                        label=""
                        value={couponState?.errorMessages || ""}
                        placeholder="Shown when coupon cannot be applied"
                        multiline
                        minRows={4}
                        fullWidth
                        size="small"
                        onChange={(e) => setCouponState((prev) => ({ ...prev, errorMessages: e.target.value }))}
                        sx={{ ...fieldSx, "& .MuiInputBase-inputMultiline": { padding: "10px 14px" } }}
                      />
                    </FormFieldGroup>
                  </FormGrid>
                </AccordionDetails>
              </Accordion>

            </Box>
          </WorkspacePanel>

          {/* ── Sticky action bar ── */}
          <Box
            sx={{
              position: "sticky",
              bottom: 16,
              zIndex: 10,
              mt: 3,
              px: 3,
              py: 1.75,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: "0 -2px 0 0 rgba(0,0,0,0.03), 0 8px 32px -4px rgba(0,0,0,0.14)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
              <Chip label={isActive ? "Active" : "Inactive"} color={isActive ? "success" : "default"} size="small" sx={{ fontSize: "0.7rem" }} />
              <Chip label={visibleOnApp ? "Visible on App" : "Hidden"} color={visibleOnApp ? "info" : "default"} size="small" sx={{ fontSize: "0.7rem" }} />
              {couponState?.couponCode && (
                <Chip label={couponState.couponCode} color="primary" variant="outlined" size="small" sx={{ fontSize: "0.7rem", fontFamily: "monospace", fontWeight: 700 }} />
              )}
              {(removeDateRule || removeUserRule || removeTimeRule) && (
                <Chip
                  label={`removing: ${[removeDateRule && "date", removeUserRule && "user", removeTimeRule && "time"].filter(Boolean).join(", ")}`}
                  color="error"
                  size="small"
                  sx={{ fontSize: "0.7rem" }}
                />
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              sx={{ px: 5, fontWeight: 500, fontSize: "14px", textTransform: "none", backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" } }}
            >
              Save Changes
            </Button>
          </Box>

        </WorkspaceContainer>
        </Box>

        {/* ── Live customer-app preview ── */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            position: "sticky",
            top: 16,
            alignSelf: "flex-start",
            display: { xs: "none", lg: "block" },
            p: 2,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
          }}
        >
          <CouponAppPreview
            couponCode={couponState?.couponCode}
            couponType={couponState?.couponType}
            couponValue={couponState?.couponValue}
            couponDescription={couponState?.couponDescription}
            minimumOrderValue={couponState?.minimumOrderValue}
            termsAndConditions={couponState?.termsAndConditions || []}
            errorMessages={couponState?.errorMessages}
            discountCouponSkuId={(couponState as any)?.discountCouponSkuId}
          />
        </Box>
        </Box>

        <AddDateInclusiveExclusiveInCoupon
          data={setInclusiveData}
          open={dateInclusiveExclusive}
          callback={() => setDateInclusiveExclusive(false)}
          setInclusiveData={setInclusiveData}
        />
      </>
    </Create>
  );
};

export default CouponEdit;
